import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {useTranslation} from "../../hooks/useTranslations";

import {
  dismissLocationPrompt,
  requestCurrentLocation,
  setPermissionStatus,
} from "../../features/location/locationSlice";
import { selectLocationState } from "../../features/location/locationSelectors";

const getStatusTitle = (translate, { location, permissionStatus, status }) => {
  if (location) return translate('location.accessEnabled', 'Location access enabled');
  if (status === "requesting") return translate('location.requesting', 'Requesting location access');
  if (permissionStatus === "denied" || status === "denied") return translate('location.accessBlocked', 'Location access blocked');
  if (status === "unsupported") return translate('location.accessUnsupported', 'Location is not supported');
  return translate('location.accessRequired', 'Location access required');
};

function LocationIcon() {
  return (
    <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.5 9c0 7-7.5 12-7.5 12S4.5 16 4.5 9a7.5 7.5 0 1115 0z" />
    </svg>
  );
}

function ManualInstructions() {

  const {t} = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return value === "key" ? fallback : value;
  };
  
  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <h3 className="text-sm font-bold text-text">{tr('location.manualTitle', 'Turn on location manually')}</h3>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-text-muted">
        <div>
          <p className="font-semibold text-text">{tr('location.chromeTitle', 'Chrome or Edge')}</p>
          <p className="mt-1">{tr('location.chromeDescription', 'Click the lock icon in the address bar, open Site settings, set Location to Allow, then refresh this page.')}</p>
        </div>
        <div>
          <p className="font-semibold text-text">{tr('location.firefoxTitle', 'Firefox')}</p>
          <p className="mt-1">{tr('location.firefoxDescription', 'Click the location icon in the address bar, clear the blocked permission, then request location again.')}</p>
        </div>
        <div>
          <p className="font-semibold text-text">{tr('location.androidTitle', 'Android')}</p>
          <p className="mt-1">{tr('location.androidDescription', 'Open browser settings, go to Site settings, choose Location, and allow this website.')}</p>
        </div>
        <div>
          <p className="font-semibold text-text">{tr('location.iosTitle', 'iPhone or iPad')}</p>
          <p className="mt-1">{tr('location.iosDescription', 'Open Settings, choose the browser, turn Location on, then allow this website when prompted.')}</p>
        </div>
      </div>
    </div>
  );
}

function LocationDetails({ location }) {
  const {t} = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return value === "key" ? fallback : value;
  };

  if (!location) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="rounded-xl border border-border bg-background p-4">
        <p className="text-xs font-bold text-text-muted uppercase tracking-wider">{tr('location.latitude', 'Latitude')}</p>
        <p className="mt-2 text-sm font-semibold text-text break-words">{location.latitude}</p>
      </div>
      <div className="rounded-xl border border-border bg-background p-4">
        <p className="text-xs font-bold text-text-muted uppercase tracking-wider">{tr('location.longitude', 'Longitude')}</p>
        <p className="mt-2 text-sm font-semibold text-text break-words">{location.longitude}</p>
      </div>
      <div className="rounded-xl border border-border bg-background p-4">
        <p className="text-xs font-bold text-text-muted uppercase tracking-wider">{tr('location.accuracy', 'Accuracy')}</p>
        <p className="mt-2 text-sm font-semibold text-text break-words">
          {Math.round(location.accuracy)} {tr('location.meters', 'meters')}
        </p>
      </div>
    </div>
  );
}

function LocationPermissionContent({ compact = false, onClose }) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return value === "key" ? fallback : value;
  };
  const { location, permissionStatus, status, message } = useSelector(selectLocationState);
  const isRequesting = status === "requesting";

  useEffect(() => {
    if (!navigator.permissions?.query) return;

    let permissionStatusRef;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((nextPermissionStatus) => {
        permissionStatusRef = nextPermissionStatus;
        dispatch(setPermissionStatus(nextPermissionStatus.state));
        permissionStatusRef.onchange = () => {
          dispatch(setPermissionStatus(permissionStatusRef.state));
        };
      })
      .catch(() => {});

    return () => {
      if (permissionStatusRef) permissionStatusRef.onchange = null;
    };
  }, [dispatch]);

  const handleRequestLocation = () => {
    dispatch(requestCurrentLocation());
  };

  const handleClose = () => {
    dispatch(dismissLocationPrompt());
    if (onClose) onClose();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 rounded-xl border border-border bg-background p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center border border-accent/20 shrink-0">
            <LocationIcon />
          </div>
          <div>
            <p className="text-sm font-bold text-text">
              {getStatusTitle(tr, { location, permissionStatus, status })}
            </p>
            <p className="text-sm text-text-muted mt-1">
              {message || tr('location.browserPermissionMessage', 'Click the button to allow location access from your browser.')}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {compact && (
            <button
              onClick={handleClose}
              className="w-full sm:w-auto flex items-center justify-center px-5 py-3 rounded-xl border border-border bg-surface text-text font-semibold text-sm hover:bg-surface-hover transition-all active:scale-95"
            >
              {tr('location.notNow', 'Not now')}
            </button>
          )}
          <button
            onClick={handleRequestLocation}
            disabled={isRequesting}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-orange-600 text-white font-semibold py-3 px-5 rounded-xl transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            {isRequesting ? tr('location.checking', 'Checking...') : tr('location.allowLocation', 'Allow Location')}
          </button>
        </div>
      </div>

      {!compact && <LocationDetails location={location} />}
      <ManualInstructions />
    </div>
  );
}

function LocationPermission({ variant = "section", onClose }) {
  const {t} = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return value === "key" ? fallback : value;
  };
  if (variant === "modal") {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 py-6">
        <section className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-surface rounded-2xl shadow-xl border border-border">
          <div className="p-5 sm:p-6 border-b border-border flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-text">{tr('location.permissionTitle', 'Location Permission')}</h2>
              <p className="text-sm text-text-muted mt-1">
                {tr('location.permissionDescription', 'Location is needed across this website for location-based workflows.')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-border text-text-muted hover:text-text hover:bg-surface-hover flex items-center justify-center transition-colors shrink-0"
              title={tr('location.close', 'Close')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-5 sm:p-6">
            <LocationPermissionContent compact onClose={onClose} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <section className="bg-surface rounded-2xl shadow-sm border border-border overflow-hidden">
      <div className="p-5 sm:p-6 border-b border-border">
        <h2 className="text-xl font-bold text-text">{tr('location.title', 'Location Settings')}</h2>
        <p className="text-sm text-text-muted mt-1">
          {tr('location.subtitle', 'Allow location access so the website can use your current position when needed.')}
        </p>
      </div>
      <div className="p-5 sm:p-6">
        <LocationPermissionContent />
      </div>
    </section>
  );
}

export default LocationPermission;
